import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { metricsService } from '../../../services/metricsService';
import { referralService } from '../../../services/referralService';

const AnalyticsTab = ({ practitionerId }) => {
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [topReferrers, setTopReferrers] = useState([]);
  const [monthlyTrends, setMonthlyTrends] = useState([]);

  useEffect(() => {
    loadAnalytics();
  }, [practitionerId]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);

      // Load statistics
      const stats = await metricsService?.getStatistics(practitionerId);
      setStatistics(stats);

      // Load all referrals
      const allReferrals = await referralService?.getAll(practitionerId);
      setReferrals(allReferrals);

      // Calculate top referrers
      const referrerCounts = {};
      allReferrals?.forEach(ref => {
        const name = ref?.referringDentistName || 'Inconnu';
        referrerCounts[name] = (referrerCounts?.[name] || 0) + 1;
      });
      
      const topList = Object.entries(referrerCounts)
        ?.map(([name, count]) => ({ name, count }))
        ?.sort((a, b) => b?.count - a?.count)
        ?.slice(0, 5);
      setTopReferrers(topList);

      // Calculate monthly trends (last 6 months)
      const monthlyData = {};
      const now = new Date();
      
      for (let i = 5; i >= 0; i--) {
        const date = new Date(now?.getFullYear(), now?.getMonth() - i, 1);
        const monthKey = date?.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
        monthlyData[monthKey] = 0;
      }

      allReferrals?.forEach(ref => {
        const refDate = new Date(ref?.submissionDate);
        const monthKey = refDate?.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
        if (monthlyData?.hasOwnProperty(monthKey)) {
          monthlyData[monthKey]++;
        }
      });

      const trends = Object.entries(monthlyData)?.map(([month, count]) => ({ month, count }));
      setMonthlyTrends(trends);

    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  const maxTrendValue = Math.max(...monthlyTrends?.map(t => t?.count), 1);

  return (
    <div className="space-y-8">
      {/* Overview Statistics */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <Icon name="Activity" size={20} className="text-accent" />
          Vue d'ensemble
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-accent/20 to-accent/10 rounded-lg p-6 border border-accent/30">
            <div className="flex items-center justify-between mb-2">
              <Icon name="FileText" size={24} className="text-accent" />
              <span className="text-3xl font-bold text-primary">{statistics?.total || 0}</span>
            </div>
            <p className="text-sm font-medium text-secondary">Total des adressages</p>
          </div>
          
          <div className="bg-gradient-to-br from-success/20 to-success/10 rounded-lg p-6 border border-success/30">
            <div className="flex items-center justify-between mb-2">
              <Icon name="CheckCircle" size={24} className="text-success" />
              <span className="text-3xl font-bold text-primary">{statistics?.completed || 0}</span>
            </div>
            <p className="text-sm font-medium text-secondary">Cas terminés</p>
          </div>
          
          <div className="bg-gradient-to-br from-warning/20 to-warning/10 rounded-lg p-6 border border-warning/30">
            <div className="flex items-center justify-between mb-2">
              <Icon name="Clock" size={24} className="text-warning" />
              <span className="text-3xl font-bold text-primary">{statistics?.pending || 0}</span>
            </div>
            <p className="text-sm font-medium text-secondary">En attente</p>
          </div>
          
          <div className="bg-gradient-to-br from-purple-500/20 to-purple-500/10 rounded-lg p-6 border border-purple-500/30">
            <div className="flex items-center justify-between mb-2">
              <Icon name="Users" size={24} className="text-purple-400" />
              <span className="text-3xl font-bold text-primary">{topReferrers?.length || 0}</span>
            </div>
            <p className="text-sm font-medium text-secondary">Dentistes actifs</p>
          </div>
        </div>
      </div>

      {/* Monthly Trends */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <Icon name="TrendingUp" size={20} className="text-accent" />
          Tendances mensuelles
        </h3>
        <div className="bg-card-elevated rounded-lg p-6 border border-white/8">
          <div className="space-y-3">
            {monthlyTrends?.map((trend, index) => (
              <div key={index} className="flex items-center gap-4">
                <span className="text-sm font-medium text-secondary w-24">{trend?.month}</span>
                <div className="flex-1 bg-card rounded-full h-8 overflow-hidden border border-white/8">
                  <div 
                    className="h-full bg-gradient-to-r from-accent to-accent/80 flex items-center justify-end px-3"
                    style={{ width: `${(trend?.count / maxTrendValue) * 100}%`, minWidth: trend?.count > 0 ? '40px' : '0' }}
                  >
                    <span className="text-xs font-semibold text-white">{trend?.count}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Referring Dentists */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <Icon name="Award" size={20} className="text-accent" />
          Dentistes les plus actifs
        </h3>
        <div className="bg-card-elevated rounded-lg overflow-hidden border border-white/8">
          {topReferrers?.length > 0 ? (
            <div className="divide-y divide-white/8">
              {topReferrers?.map((referrer, index) => (
                <div key={index} className="flex items-center justify-between p-4 hover:bg-card-hover transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      index === 0 ? 'bg-warning/20 text-warning' :
                      index === 1 ? 'bg-muted text-secondary' :
                      index === 2 ? 'bg-orange-500/20 text-orange-400': 'bg-accent/20 text-accent'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-primary">{referrer?.name}</p>
                      <p className="text-sm text-secondary">{referrer?.count} adressage{referrer?.count > 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <Icon name="ChevronRight" size={20} className="text-muted" />
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Icon name="Users" size={48} className="text-muted mx-auto mb-3" />
              <p className="text-secondary">Aucun adressage pour le moment</p>
            </div>
          )}
        </div>
      </div>

      {/* Status Breakdown */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <Icon name="PieChart" size={20} className="text-accent" />
          Répartition par statut
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-accent/10 rounded-lg p-4 border border-accent/30">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-secondary">Nouveaux</span>
              <span className="text-2xl font-bold text-accent">{statistics?.new || 0}</span>
            </div>
          </div>
          <div className="bg-warning/10 rounded-lg p-4 border border-warning/30">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-secondary">Revus</span>
              <span className="text-2xl font-bold text-warning">{statistics?.reviewed || 0}</span>
            </div>
          </div>
          <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/30">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-secondary">Planifiés</span>
              <span className="text-2xl font-bold text-purple-400">{statistics?.scheduled || 0}</span>
            </div>
          </div>
          <div className="bg-success/10 rounded-lg p-4 border border-success/30">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-secondary">Terminés</span>
              <span className="text-2xl font-bold text-success">{statistics?.completed || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsTab;